// Maps (elementKey, slot) -> existing Redux setter/clearer in CourseMapSlice.
// Isolates the engine from the slice's per-element naming convention so the
// save pipeline (CourseMap.jsx) and util label converters keep working
// untouched.
//
// Some elements share generic slots (e.g. Vegetation/Rock dispatch
// shape-properties* setters today). The bindings keep that wiring stable.
//
// Clearers can be undefined for slots that have no paired clear reducer in
// the slice (Background, Fixtures). The engine no-ops in those cases.

import {
  setCourseBridgePropertiesDesc,
  clearCourseBridgePropertiesDesc,
  setCourseBuildingDesc,
  clearCourseBuildingDesc,
  setCourseBuildingType,
  clearCourseBuildingType,
  setCourseBuildingSize,
  clearCourseBuildingSize,
  setCourseCliffDesc,
  clearCourseCliffDesc,
  setCourseCliffType,
  clearCourseCliffType,
  setCourseClubhouseDesc,
  clearCourseClubhouseDesc,
  setCourseClubhouseType,
  clearCourseClubhouseType,
  setCourseClubhouseSize,
  clearCourseClubhouseSize,
  setCourseCreekSize,
  clearCourseCreekSize,
  setCourseFenceDesc,
  clearCourseFenceDesc,
  setCourseFenceSize,
  clearCourseFenceSize,
  setCourseFixtureSize,
  setCourseFixtureType,
  setCourseForestType,
  clearCourseForestType,
  setCourseLavaType,
  clearCourseLavaType,
  setCourseMarkerHazardTreeDesc,
  clearCourseMarkerHazardTreeDesc,
  setCourseMarkerHazardTreeSize,
  clearCourseMarkerHazardTreeSize,
  setCourseMarkerHazardTreeType,
  clearCourseMarkerHazardTreeType,
  setCourseNativeAreaDesc,
  clearCourseNativeAreaDesc,
  setCourseNativeAreaType,
  clearCourseNativeAreaType,
  setCourseNativeAreaSize,
  clearCourseNativeAreaSize,
  setCourseParkingLotDesc,
  clearCourseParkingLotDesc,
  setCoursePathDesc,
  clearCoursePathDesc,
  setCoursePolygonPropertiesDesc,
  clearCoursePolygonPropertiesDesc,
  setCoursePolygonPropertiesType,
  clearCoursePolygonPropertiesType,
  setCourseResidenceDesc,
  clearCourseResidenceDesc,
  setCourseResidenceType,
  clearCourseResidenceType,
  setCourseResidenceSize,
  clearCourseResidenceSize,
  setCourseShapeBackgroundDesc,
  setCourseShapeBackgroundType,
  setCourseShapePropertiesSize,
  clearCourseShapePropertiesSize,
  setCourseShapePropertiesType,
  clearCourseShapePropertiesType,
  setCourseStreetDesc,
  clearCourseStreetDesc,
  setCourseStreetSize,
  clearCourseStreetSize,
  setCourseWaterDesc,
  clearCourseWaterDesc,
  setCourseWaterType,
  clearCourseWaterType,
  setCourseWaterSize,
  clearCourseWaterSize,
} from "redux/CourseMapSlice";

// payloadKey: the legacy event-payload field name that
// CourseMap.jsx#saveNewShape reads via `e?.<payloadKey>`. We keep emitting
// these so the save pipeline stays untouched.
const bind = (set, clear, payloadKey) => ({ set, clear: clear || null, payloadKey });

export const attributeBindings = {
  Background: {
    description: bind(setCourseShapeBackgroundDesc, null, "backgroundShapeDesc"),
    type: bind(setCourseShapeBackgroundType, null, "backgroundShapeType"),
  },
  CartPath: {
    description: bind(setCoursePathDesc, clearCoursePathDesc, "pathDescValue"),
  },
  Residence: {
    description: bind(setCourseResidenceDesc, clearCourseResidenceDesc, "residenceDescValue"),
    type: bind(setCourseResidenceType, clearCourseResidenceType, "residenceTypeValue"),
    size: bind(setCourseResidenceSize, clearCourseResidenceSize, "residenceSizeValue"),
  },
  Condo: {
    description: bind(setCourseBuildingDesc, clearCourseBuildingDesc, "buildingDescValue"),
    type: bind(setCourseBuildingType, clearCourseBuildingType, "buildingTypeValue"),
    size: bind(setCourseBuildingSize, clearCourseBuildingSize, "buildingSizeValue"),
  },
  Hotel: {
    description: bind(setCourseBuildingDesc, clearCourseBuildingDesc, "buildingDescValue"),
    type: bind(setCourseBuildingType, clearCourseBuildingType, "buildingTypeValue"),
    size: bind(setCourseBuildingSize, clearCourseBuildingSize, "buildingSizeValue"),
  },
  Business: {
    description: bind(setCourseBuildingDesc, clearCourseBuildingDesc, "buildingDescValue"),
    type: bind(setCourseBuildingType, clearCourseBuildingType, "buildingTypeValue"),
    size: bind(setCourseBuildingSize, clearCourseBuildingSize, "buildingSizeValue"),
  },
  ParkingLot: {
    description: bind(setCourseParkingLotDesc, clearCourseParkingLotDesc, "parkingLotDescValue"),
  },
  Street: {
    description: bind(setCourseStreetDesc, clearCourseStreetDesc, "streetDescValue"),
    size: bind(setCourseStreetSize, clearCourseStreetSize, "streetSizeValue"),
  },
  Fence: {
    description: bind(setCourseFenceDesc, clearCourseFenceDesc, "fenceDescValue"),
    size: bind(setCourseFenceSize, clearCourseFenceSize, "fenceSizeValue"),
  },
  PracticeBunker: {
    description: bind(setCoursePolygonPropertiesDesc, clearCoursePolygonPropertiesDesc, "polygonDesc"),
    type: bind(setCoursePolygonPropertiesType, clearCoursePolygonPropertiesType, "polygonType"),
  },
  Tree: {
    description: bind(setCourseMarkerHazardTreeDesc, clearCourseMarkerHazardTreeDesc, "treeMarkerDesc"),
    type: bind(setCourseMarkerHazardTreeType, clearCourseMarkerHazardTreeType, "treeMarkerType"),
    size: bind(setCourseMarkerHazardTreeSize, clearCourseMarkerHazardTreeSize, "treeMarkerSize"),
  },
  Water: {
    description: bind(setCourseWaterDesc, clearCourseWaterDesc, "waterDescValue"),
    type: bind(setCourseWaterType, clearCourseWaterType, "waterTypeValue"),
    size: bind(setCourseWaterSize, clearCourseWaterSize, "waterSizeValue"),
  },
  Vegetation: {
    type: bind(setCourseShapePropertiesType, clearCourseShapePropertiesType, "markerType"),
    size: bind(setCourseShapePropertiesSize, clearCourseShapePropertiesSize, "selectedMarkerMod2"),
  },
  Bridge: {
    description: bind(setCourseBridgePropertiesDesc, clearCourseBridgePropertiesDesc, "bridgeDesc"),
  },
  Sand: {
    description: bind(setCoursePolygonPropertiesDesc, clearCoursePolygonPropertiesDesc, "polygonDesc"),
    type: bind(setCoursePolygonPropertiesType, clearCoursePolygonPropertiesType, "polygonType"),
  },
  Rock: {
    size: bind(setCourseShapePropertiesSize, clearCourseShapePropertiesSize, "selectedMarkerMod2"),
  },
  Lava: {
    type: bind(setCourseLavaType, clearCourseLavaType, "lavaTypeValue"),
  },
  Cliff: {
    description: bind(setCourseCliffDesc, clearCourseCliffDesc, "cliffPolygonDesc"),
    type: bind(setCourseCliffType, clearCourseCliffType, "cliffPolygonType"),
  },
  NativeArea: {
    description: bind(setCourseNativeAreaDesc, clearCourseNativeAreaDesc, "nativeAreaPolygonDesc"),
    type: bind(setCourseNativeAreaType, clearCourseNativeAreaType, "nativeAreaPolygonType"),
    size: bind(setCourseNativeAreaSize, clearCourseNativeAreaSize, "nativeAreaPolygonSize"),
  },
  Fixtures: {
    type: bind(setCourseFixtureType, null, "fixtureMarkerType"),
    size: bind(setCourseFixtureSize, null, "fixtureMarkerSize"),
  },
  Forest: {
    type: bind(setCourseForestType, clearCourseForestType, "forestPolygonType"),
  },
  Bunker: {
    description: bind(setCoursePolygonPropertiesDesc, clearCoursePolygonPropertiesDesc, "polygonDesc"),
    type: bind(setCoursePolygonPropertiesType, clearCoursePolygonPropertiesType, "polygonType"),
  },
  Clubhouse: {
    description: bind(setCourseClubhouseDesc, clearCourseClubhouseDesc, "clubhouseDescValue"),
    type: bind(setCourseClubhouseType, clearCourseClubhouseType, "clubhouseTypeValue"),
    size: bind(setCourseClubhouseSize, clearCourseClubhouseSize, "clubhouseSizeValue"),
  },
  Creek: {
    size: bind(setCourseCreekSize, clearCourseCreekSize, "creekSizeValue"),
  },
};

export const getElementBinding = (elementKey, slot) => {
  const e = attributeBindings[elementKey];
  return e ? e[slot] || null : null;
};
